/**
 * Loader and typed facade for the Zendesk messaging Web Widget (the support
 * chat). The snippet is the one Zendesk hands out:
 *
 *   <script id="ze-snippet" src="https://static.zdassets.com/ekr/snippet.js?key=…">
 *
 * It is injected from here rather than pasted into `index.html` for three
 * reasons:
 *
 * 1. Only people with a session talk to support from inside the product, so the
 *    third-party script is not downloaded on `/login`.
 * 2. Unit tests (jsdom) and Playwright runs must not reach out to Zendesk: a
 *    real widget in an automated browser opens anonymous conversations in the
 *    support team's inbox and paints a floating launcher over the layouts the
 *    responsive specs measure.
 * 3. The widget starts HIDDEN (`messenger hide`). The way in is the button in
 *    the header (`SupportChatButton`), as in AGRI (farm-manager), so Zendesk's
 *    own floating launcher never covers a table's last row or a dialog's
 *    primary button.
 */

/** The key of the «factit-tcit» widget. It is not a secret: it ships in every
 * page that loads the widget. `VITE_ZENDESK_WIDGET_KEY` overrides it, and an
 * EMPTY value turns the chat off for that build. */
const DEFAULT_WIDGET_KEY = 'cf6280f5-7a99-4473-8c5a-c8744f6ec148';

const SNIPPET_ID = 'ze-snippet';
const SNIPPET_ORIGIN = 'https://static.zdassets.com/ekr/snippet.js';

/** The slice of the `zE` API this app uses (messaging Web Widget, not the
 * legacy Classic widget, whose commands are `zE('webWidget', …)`). */
export interface ZendeskApi {
  (command: 'messenger', action: 'open' | 'close' | 'show' | 'hide' | 'logoutUser'): void;
  (
    command: 'messenger',
    action: 'loginUser',
    jwtCallback: (callback: (token: string) => void) => void,
  ): void;
  (command: 'messenger:set', setting: 'locale', value: string): void;
  (command: 'messenger:set', setting: 'zIndex', value: number): void;
  (command: 'messenger:on', event: 'open' | 'close', callback: () => void): void;
  (
    command: 'messenger:on',
    event: 'unreadMessages',
    callback: (count: number) => void,
  ): void;
}

declare global {
  interface Window {
    zE?: ZendeskApi;
  }
}

export function zendeskWidgetKey(): string {
  const configured = import.meta.env.VITE_ZENDESK_WIDGET_KEY;
  return configured === undefined ? DEFAULT_WIDGET_KEY : configured.trim();
}

/**
 * Whether this build and this browser should load the widget at all. Off with
 * an empty key, under Vitest, and in a browser driven by automation
 * (`navigator.webdriver`, which Playwright sets): see point 2 above.
 */
export function isZendeskEnabled(): boolean {
  if (zendeskWidgetKey() === '') return false;
  if (import.meta.env.MODE === 'test') return false;
  if (typeof navigator !== 'undefined' && navigator.webdriver) return false;
  return true;
}

let pending: Promise<ZendeskApi> | null = null;

/**
 * A `zE` that looks `window.zE` up on every call instead of holding on to one
 * function.
 *
 * The snippet first defines `window.zE` as a stub that only queues its
 * arguments (`function(){e.push(arguments)}`), and REPLACES it with the real
 * API once the widget has booted, after draining the queue. A reference taken
 * when the script's `load` fires is that stub, so every command sent through
 * it after the boot lands in a queue nobody reads any more. That is how the
 * first release shipped: `hide` and `loginUser` worked (queued before the boot)
 * and the header button's `show` + `open` were silently dropped. Observed
 * against prod by recording every assignment to `window.zE`.
 */
const liveZendesk = ((...args: unknown[]) => {
  (window.zE as unknown as (...a: unknown[]) => void)(...args);
}) as ZendeskApi;

/**
 * Injects the snippet once and resolves with `window.zE` when it has loaded.
 * Every caller shares the same promise; a failed load (an ad blocker, no
 * network) clears it so a later mount can try again.
 */
export function loadZendesk(): Promise<ZendeskApi> {
  if (window.zE) return Promise.resolve(liveZendesk);
  if (pending) return pending;

  pending = new Promise<ZendeskApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = SNIPPET_ID;
    script.async = true;
    script.src = `${SNIPPET_ORIGIN}?key=${encodeURIComponent(zendeskWidgetKey())}`;
    script.addEventListener('load', () => {
      if (window.zE) {
        resolve(liveZendesk);
      } else {
        reject(new Error('Zendesk snippet loaded but did not define window.zE'));
      }
    });
    script.addEventListener('error', () => {
      script.remove();
      reject(new Error('Zendesk snippet failed to load'));
    });
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    pending = null;
    throw error;
  });

  return pending;
}
